import { create } from "zustand";
import { persist } from "zustand/middleware";

// ── Pricing config (simulates a backend pricing service; never hardcoded in the UI layer) ──
export type TierId = "free" | "standard" | "premium";

export interface PricingTier {
  id: TierId;
  name: string;
  amountZar: number;
}

const PRICING: PricingTier[] = [
  { id: "free", name: "Free", amountZar: 0 },
  { id: "standard", name: "Standard", amountZar: 1499 },
  { id: "premium", name: "Premium", amountZar: 3499 },
];

// Add-ons stay behind a feature flag until confirmed.
export const ADDONS_ENABLED = false;

export interface AddOn {
  id: string;
  label: string;
  amountZar: number;
}

const ADDONS: AddOn[] = [
  { id: "quote_run", label: "Quote Analyser run", amountZar: 199 },
  { id: "chat_10", label: "10 chat credits", amountZar: 99 },
];

export type UsageFeature = { used: number; limit: number } | "coming_soon" | null;

export interface Usage {
  planRuns: UsageFeature;
  quoteRuns: UsageFeature;
  chatCredits: UsageFeature;
  homeVision: UsageFeature;
}

function usageForTier(tier: TierId): Usage {
  switch (tier) {
    case "premium":
      return {
        planRuns: { used: 3, limit: 50 },
        quoteRuns: { used: 1, limit: 25 },
        chatCredits: { used: 40, limit: 200 },
        homeVision: "coming_soon",
      };
    case "standard":
      return {
        planRuns: { used: 2, limit: 15 },
        quoteRuns: { used: 1, limit: 8 },
        chatCredits: { used: 12, limit: 60 },
        homeVision: null,
      };
    default:
      return {
        planRuns: { used: 1, limit: 3 },
        quoteRuns: null,
        chatCredits: null,
        homeVision: null,
      };
  }
}

export type EntitlementStatus = "free" | "active" | "confirming" | "failed" | "refunded";

export interface ProjectEntitlement {
  projectId: string;
  tier: TierId;
  amountZar: number;
  paidAt: string | null;
  payfastPaymentId: string | null;
  usage: Usage;
  status: EntitlementStatus;
  pendingTier?: TierId;
  confirmingSince?: number;
}

export type ChargeStatus = "confirmed" | "pending" | "failed" | "refunded";

export interface BillingCharge {
  id: string;
  projectId: string;
  projectName: string;
  item: string;
  amountZar: number;
  date: string; // ISO
  status: ChargeStatus;
}

interface BillingState {
  entitlements: Record<string, ProjectEntitlement>;
  charges: Record<string, BillingCharge[]>;
  seededFirst: boolean;
  ensure: (projectId: string, projectName: string, isOldest?: boolean) => ProjectEntitlement;
  beginUpgrade: (projectId: string, tier: TierId) => void;
  finalizeUpgrade: (projectId: string, projectName: string, tier: TierId) => void;
  failPayment: (projectId: string) => void;
  requestRefund: (projectId: string, projectName: string) => void;
}

function freeEntitlement(projectId: string): ProjectEntitlement {
  return {
    projectId,
    tier: "free",
    amountZar: 0,
    paidAt: null,
    payfastPaymentId: null,
    usage: usageForTier("free"),
    status: "free",
  };
}

// Seeded paid entitlement + history for the first project, so the screen demos richly.
function seededStandard(projectId: string, projectName: string): { ent: ProjectEntitlement; charges: BillingCharge[] } {
  const paidAt = "2026-10-29T10:12:00+02:00";
  return {
    ent: {
      projectId,
      tier: "standard",
      amountZar: 1499,
      paidAt,
      payfastPaymentId: "pf_1829304756",
      usage: usageForTier("standard"),
      status: "active",
    },
    charges: [
      { id: "ch_3", projectId, projectName, item: "Standard plan", amountZar: 1499, date: paidAt, status: "confirmed" },
      { id: "ch_2", projectId, projectName, item: "Quote Analyser run", amountZar: 199, date: "2026-10-18T14:03:00+02:00", status: "confirmed" },
      { id: "ch_1", projectId, projectName, item: "10 chat credits", amountZar: 99, date: "2026-10-11T09:41:00+02:00", status: "pending" },
    ],
  };
}

export const useBillingStore = create<BillingState>()(
  persist(
    (set, get) => ({
      entitlements: {},
      charges: {},
      seededFirst: false,
      ensure: (projectId, projectName, isOldest = false) => {
        const existing = get().entitlements[projectId];
        if (existing) return existing;
        if (isOldest && !get().seededFirst) {
          const { ent, charges } = seededStandard(projectId, projectName);
          set((s) => ({
            entitlements: { ...s.entitlements, [projectId]: ent },
            charges: { ...s.charges, [projectId]: charges },
            seededFirst: true,
          }));
          return ent;
        }
        const ent = freeEntitlement(projectId);
        set((s) => ({ entitlements: { ...s.entitlements, [projectId]: ent } }));
        return ent;
      },
      beginUpgrade: (projectId, tier) =>
        set((s) => {
          const prev = s.entitlements[projectId] ?? freeEntitlement(projectId);
          // Entitlement (tier/amount) must NOT change until the ITN confirms; only mark confirming.
          return {
            entitlements: {
              ...s.entitlements,
              [projectId]: { ...prev, status: "confirming", pendingTier: tier, confirmingSince: Date.now() },
            },
          };
        }),
      finalizeUpgrade: (projectId, projectName, tier) =>
        set((s) => {
          const price = PRICING.find((p) => p.id === tier)?.amountZar ?? 0;
          const paidAt = new Date().toISOString();
          const paymentId = `pf_${Math.floor(Math.random() * 9e9 + 1e9)}`;
          const ent: ProjectEntitlement = {
            projectId,
            tier,
            amountZar: price,
            paidAt,
            payfastPaymentId: paymentId,
            usage: usageForTier(tier),
            status: "active",
          };
          const charge: BillingCharge = {
            id: `ch_${Date.now()}`,
            projectId,
            projectName,
            item: `${PRICING.find((p) => p.id === tier)?.name} plan`,
            amountZar: price,
            date: paidAt,
            status: "confirmed",
          };
          return {
            entitlements: { ...s.entitlements, [projectId]: ent },
            charges: { ...s.charges, [projectId]: [charge, ...(s.charges[projectId] ?? [])] },
          };
        }),
      failPayment: (projectId) =>
        set((s) => {
          const prev = s.entitlements[projectId];
          if (!prev) return s;
          return { entitlements: { ...s.entitlements, [projectId]: { ...prev, status: "failed" } } };
        }),
      requestRefund: (projectId, projectName) =>
        set((s) => {
          const prev = s.entitlements[projectId];
          if (!prev) return s;
          const refund: BillingCharge = {
            id: `ch_${Date.now()}`,
            projectId,
            projectName,
            item: `Refund · ${PRICING.find((p) => p.id === prev.tier)?.name} plan`,
            amountZar: prev.amountZar,
            date: new Date().toISOString(),
            status: "refunded",
          };
          const reverted: ProjectEntitlement = { ...freeEntitlement(projectId), status: "refunded" };
          return {
            entitlements: { ...s.entitlements, [projectId]: reverted },
            charges: { ...s.charges, [projectId]: [refund, ...(s.charges[projectId] ?? [])] },
          };
        }),
    }),
    { name: "buildease.billing" },
  ),
);

// ── Pricing-service accessors (UI reads prices only through these) ──
export const getPricing = () => PRICING;
export const getTier = (id: TierId) => PRICING.find((p) => p.id === id)!;
export const getAddOns = () => ADDONS;

export function getUpgradeTarget(tier: TierId): PricingTier | null {
  const order: TierId[] = ["free", "standard", "premium"];
  const next = order[order.indexOf(tier) + 1];
  return next ? getTier(next) : null;
}

export const formatRand = (n: number) => `R${n.toLocaleString("en-US")}`;

export function formatSADate(iso: string): string {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

export function allCharges(charges: Record<string, BillingCharge[]>): BillingCharge[] {
  return Object.values(charges)
    .flat()
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}
